import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material';
import { fontWeight } from '../../../node_modules/@mui/system/index';

const GoalStaticsTotalTable = ({ loading, data, tableRef, deptList }) => {
  const rowDefinitions = [
    { label: '매출 목표', key: 'statAmt' },
    { label: '매출이익 목표', key: 'statProfit' },
    { label: '영업이익 목표', key: 'statOperatingProfit' },
  ];

  const { dataMap, totalMap } = useMemo(() => {
    const safeData = data?.list && Array.isArray(data.list) ? data.list : [];
    const dMap = {}; 
    const tMap = { statAmt: 0, statProfit: 0, statOperatingProfit: 0 };

    const validUuids = new Set(deptList?.map(d => d.dptUuid) || []);
    const lastIdx = deptList && deptList.length > 0 ? deptList.length - 1 : -1;
    const lastUuid = lastIdx !== -1 ? deptList[lastIdx].dptUuid : null;

    safeData.forEach(item => {
      rowDefinitions.forEach(row => {
        tMap[row.key] += (Number(item[row.key]) || 0);
      });

      const currentUuid = item.dptUuid;
      if (validUuids.has(currentUuid)) {
        const existing = dMap[currentUuid] || { statAmt: 0, statProfit: 0, statOperatingProfit: 0 };
        dMap[currentUuid] = {
          ...item,
          statAmt: (Number(existing.statAmt) || 0) + (Number(item.statAmt) || 0),
          statProfit: (Number(existing.statProfit) || 0) + (Number(item.statProfit) || 0),
          statOperatingProfit: (Number(existing.statOperatingProfit) || 0) + (Number(item.statOperatingProfit) || 0),
        };
      } else if (lastUuid) {
        const lastDeptData = dMap[lastUuid] || { statAmt: 0, statProfit: 0, statOperatingProfit: 0 };
        dMap[lastUuid] = {
          ...lastDeptData,
          statAmt: (Number(lastDeptData.statAmt) || 0) + (Number(item.statAmt) || 0),
          statProfit: (Number(lastDeptData.statProfit) || 0) + (Number(item.statProfit) || 0),
          statOperatingProfit: (Number(lastDeptData.lastOperatingProfit) || 0) + (Number(item.statOperatingProfit) || 0),
        };
      }
    });

    return { dataMap: dMap, totalMap: tMap };
  }, [data, deptList]);

  const baseColumns = [{ id: 'total', name: 'TOTAL' }];
  const teamColumns = useMemo(() => deptList?.map(dept => ({
    id: dept.dptUuid, 
    name: dept.dptNm   
  })) || [], [deptList]);

  const columns = [...baseColumns, ...teamColumns];

  return (
    <Box sx={{ my: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, pr: 1, fontSize: '0.85rem', color: '#666' }}>
        (단위:원)
      </Box>

      <Paper elevation={1} sx={{ overflow: 'auto' }}>
        <TableContainer>
          <Table 
            size="small" 
            ref={tableRef}
            sx={{ 
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': {
                border: '1px solid #e0e0e0',
                padding: '12px 10px',
                fontSize: '0.875rem', // Detail 테이블과 동일하게 설정
                height: '40px',
                minWidth: '130px',
                lineHeight: 1.4,
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell 
                  align="center" 
                  sx={{ 
                    backgroundColor: '#e3f2fd !important', 
                    color: '#333', 
                    width: '200px', 
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    zIndex: 2
                  }}
                >
                  구분
                </TableCell>
                {columns.map((col) => (
                  <TableCell 
                    key={col.id} 
                    align="center" 
                    sx={{ 
                      backgroundColor: '#e3f2fd !important', 
                      color: '#333', 
                      fontWeight: 'bold' 
                    }}
                  >
                    {col.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rowDefinitions.map((row) => (
                <TableRow key={row.key}>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      backgroundColor: '#fafafa', 
                      fontWeight: 600, 
                      color: '#333',
                      position: 'sticky',
                      left: 0,
                      zIndex: 1
                    }}
                  >
                    {row.label}
                  </TableCell>

                  {columns.map((col) => {
                    const isTotal = col.id === 'total';
                    const val = isTotal 
                      ? totalMap[row.key] 
                      : (dataMap[col.id]?.[row.key] || 0);

                    return (
                      <TableCell 
                        key={`${row.key}-${col.id}`} 
                        align="right"
                        sx={{ 
                          backgroundColor: isTotal ? '#f8f9fa' : 'inherit',
                          fontWeight: isTotal ? 'bold' : 'normal',
                          // Detail 테이블의 숫자 스타일(폰트 크기 및 색상) 적용
                          fontSize: '0.875rem',
                          fontWeight: isTotal ? '600' : '500',
                          color: isTotal ? '#1976d2' : '#333'
                        }}
                      >
                        {Number(val).toLocaleString()}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default GoalStaticsTotalTable;
